import { parse } from 'content-disposition-header'

const getDownloadFileName = (
	response: Response,
	fallbackFileName?: string
): string => {
	const contentDisposition = response.headers.get('Content-Disposition')

	if (!contentDisposition) {
		return fallbackFileName ?? 'download'
	}

	return decodeURIComponent(parse(contentDisposition).parameters.filename)
}

export const readDownloadResponseWithProgress = async (
	response: Response,
	onProgress: (percent: number) => void,
	fallbackFileName?: string,
	abortSignal?: AbortSignal
): Promise<{ blob: Blob; fileName: string }> => {
	if (abortSignal?.aborted) {
		throw new DOMException('The operation was aborted.', 'AbortError')
	}

	const contentLength = response.headers.get('Content-Length')
	const totalLength = contentLength ? parseInt(contentLength, 10) : 0
	const reader = response.body?.getReader()

	if (!reader) {
		const blob = await response.blob()
		onProgress(100)

		return {
			blob,
			fileName: getDownloadFileName(response, fallbackFileName),
		}
	}

	const chunks: Uint8Array[] = []
	let receivedLength = 0

	let isComplete = false

	const abortRead = (): void => {
		void reader.cancel()
	}

	abortSignal?.addEventListener('abort', abortRead, { once: true })

	try {
		while (!isComplete) {
			if (abortSignal?.aborted) {
				throw new DOMException('The operation was aborted.', 'AbortError')
			}

			const { done, value } = await reader.read()
			isComplete = done

			if (done || !value) {
				continue
			}

			receivedLength += value.length
			chunks.push(value)

			if (totalLength > 0) {
				onProgress(
					parseFloat(((receivedLength / totalLength) * 100).toFixed(2))
				)
			}
		}
	} finally {
		abortSignal?.removeEventListener('abort', abortRead)
	}

	if (abortSignal?.aborted) {
		throw new DOMException('The operation was aborted.', 'AbortError')
	}

	return {
		blob: new Blob(chunks as BlobPart[]),
		fileName: getDownloadFileName(response, fallbackFileName),
	}
}
